// Sistema de audio dual: si hay un archivo cargado en el slot
// correspondiente, lo reproduce; si no, cae al sonido sintético
// generado con Web Audio API (los originales).
//
// Refactor 2026-06-02 (post code-review): unidad de configuración pasa
// de "1 slot por kind global" a "1 slot por (estilo, juego, evento)".
// La fuente de verdad de qué eventos existen es GAME_SOUND_EVENTS en
// @juegossc/shared. playEvent() hace cascada:
//   1. slot exacto `${style}-${juego}-${evento}` (subido por super_admin).
//   2. slot legacy del modelo viejo (tick / spin-start / blink /
//      win-${style} / consolation) — solo si el evento lo tenía.
//   3. sintético definido en este archivo (último fallback).
// Los wrappers viejos (playClick/playSpinStart/etc) siguen existiendo
// para back-compat — pueden borrarse cuando todos los call sites pasen
// a playEvent.
//
// Lazy init del AudioContext: requiere user gesture en algunos browsers.

import {
  GAME_SOUND_EVENTS,
  gameSoundSlotKey,
  type GameType,
  type SyntheticFallback,
} from "@juegossc/shared";

// SoundStyle es un slug arbitrario (string) que matchea un row de la
// tabla sound_styles (migration 029). Antes era enum hardcoded; ahora
// el super_admin lo gestiona desde /admin/shopping y el frontend solo
// recibe la lista para renderizar el selector y mapear a slot.
export type SoundStyle = string;

// ──────────────────────────────────────────────
// Slots FIJOS conocidos por el frontend
// ──────────────────────────────────────────────
// Estos slots son únicos (uno solo, compartido por todos los estilos).
// Los slots de "win-${slug}" son DINÁMICOS y no viven en este objeto
// — se calculan en runtime a partir del slug del estilo elegido.
//
// El backend (sound_assets.slot_key) acepta cualquier TEXT — el
// frontend solo lo dispara si conoce el play API para ese slot.
//
// Para agregar un slot nuevo FIJO (no por estilo): sumá la entrada
// acá + el playX() correspondiente. El admin lo muestra automáticamente
// porque itera sobre SOUND_SLOTS.
export const SOUND_SLOTS = {
  tick: {
    label: "Tick de la flecha",
    hint: "Suena cada vez que la flecha pasa un segmento. MP3 corto (50–150ms).",
  },
  "spin-start": {
    label: "Inicio del giro",
    hint: "Suena al apretar GIRAR. Whoosh tipo lanzamiento.",
  },
  blink: {
    label: "Parpadeo del ganador",
    hint: "Suena cada vez que el segmento ganador parpadea (3 veces).",
  },
  consolation: {
    label: "Consolation",
    hint: "Suena en 'Siga participando' / 'Casi pasa'.",
  },
  retry: {
    label: "Reintento",
    hint: "Suena al sacar un premio reintento.",
  },
} as const;

export type SoundSlot = keyof typeof SOUND_SLOTS;

// Helper: dado un slug de estilo, devuelve el slot key del archivo de
// victoria. Convención: win-${slug}. Si el super_admin sube un MP3
// con ese slot_key desde /admin/shopping, sustituye el sintético.
function winSlotForStyle(slug: SoundStyle): string {
  return `win-${slug}`;
}

// ──────────────────────────────────────────────
// AudioContext + cache de buffers
// ──────────────────────────────────────────────
let ac: AudioContext | null = null;

function getCtx(): AudioContext {
  if (!ac) {
    const Ctx =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    ac = new Ctx();
  }
  return ac;
}

// Map slot → URL (declarado por el frontend al recibir el state).
// Keys son strings libres — fijos (SOUND_SLOTS) o dinámicos (win-${slug}).
const urlMap = new Map<string, string>();
// Map slot → AudioBuffer. `null` significa "intentamos y falló o no
// había URL"; ausencia de la key significa "todavía no se intentó".
const bufferCache = new Map<string, AudioBuffer | null>();

// Gate global de habilitado de sonido (migration 029, por sucursal).
// Default true para no romper el bootstrap antes de que llegue el state.
// Cuando llega state.settings.sound_enabled, useAppState llama setSoundEnabled.
let soundEnabled = true;

export function setSoundEnabled(enabled: boolean): void {
  soundEnabled = enabled;
}

// Inyecta el map de URLs y dispara precarga async de cada uno. Llamar
// una vez en el bootstrap del app (cuando llega state.sounds del API).
// Acepta cualquier slot_key (los fijos + cualquier win-${slug}); el
// frontend solo dispara los que conoce, los demás quedan ignorados.
export function setSoundUrls(urls: Record<string, string>): void {
  for (const [k, v] of Object.entries(urls)) {
    urlMap.set(k, v);
    // Disparar la carga pero NO esperar. Si falla, queda null y se
    // usa el sintético para siempre (no retry).
    loadSound(k).catch(() => {});
  }
}

async function loadSound(slot: string): Promise<AudioBuffer | null> {
  if (bufferCache.has(slot)) return bufferCache.get(slot) ?? null;
  const url = urlMap.get(slot);
  if (!url) {
    bufferCache.set(slot, null);
    return null;
  }
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const ab = await res.arrayBuffer();
    const buf = await getCtx().decodeAudioData(ab);
    bufferCache.set(slot, buf);
    return buf;
  } catch {
    bufferCache.set(slot, null);
    return null;
  }
}

// Intenta reproducir el archivo cargado para `slot`. Si está cacheado,
// lo dispara y devuelve true. Si no (sin URL o load falló), devuelve
// false para que el caller use el fallback sintético.
function playFile(slot: string, volume = 1): boolean {
  const buf = bufferCache.get(slot);
  if (!buf) return false;
  try {
    const ctx = getCtx();
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const gain = ctx.createGain();
    gain.gain.value = volume;
    src.connect(gain);
    gain.connect(ctx.destination);
    src.start();
    return true;
  } catch {
    return false;
  }
}

// ──────────────────────────────────────────────
// API pública — un wrapper por slot
// ──────────────────────────────────────────────
// Cada función intenta primero el archivo. Si no hay, fallback al
// sintético histórico (cero breaking change).

export function playClick(intensity = 1): void {
  if (!soundEnabled) return;
  if (playFile("tick", Math.min(1, intensity))) return;
  syntheticClick(intensity);
}

export function playSpinStart(): void {
  if (!soundEnabled) return;
  if (playFile("spin-start")) return;
  syntheticSpinStart();
}

export function playBlink(): void {
  if (!soundEnabled) return;
  if (playFile("blink")) return;
  syntheticBlink();
}

export function playRetry(): void {
  if (!soundEnabled) return;
  if (playFile("retry")) return;
  // No hay sintético para retry — antes usaba la fanfarria normal.
  // Mantenemos eso para no cambiar el feel hasta que carguen archivo.
  playFanfarria("classic");
}

export function playFanfarria(style: SoundStyle = "classic"): void {
  if (!soundEnabled) return;
  // 'silent' es un slug reservado — significa "no suena nada".
  if (style === "silent") return;
  // Buscar archivo subido para este estilo (slot win-${slug}). Si
  // existe, lo reproducimos y listo.
  if (playFile(winSlotForStyle(style))) return;
  // Fallback sintético. Para los 5 slugs históricos preservamos las
  // melodías originales (classic/casino/festivo/mundial). Para un
  // slug NUEVO creado por super_admin sin archivo cargado, cae al
  // sintético classic — no es lo ideal pero es mejor que silencio.
  // El super_admin debería subir un MP3 para que su estilo nuevo
  // tenga sonido propio.
  //
  // §20 stale-helper: el switch asume los 5 seeds históricos. Si el
  // super_admin BORRA uno de esos seeds y crea otro con el MISMO slug
  // ('casino' con otro mood), el cliente igual va a escuchar la
  // fanfarria 8-bit del seed original — surpresa silenciosa. Para
  // resolverlo de raíz hay que mover el mapping slug→synth-kind a la
  // tabla DB (columna `synth_fallback_kind`). Mientras tanto, asumimos
  // que los 5 slugs históricos no se renombran.
  try {
    const ctx = getCtx();
    switch (style) {
      case "casino":
        fanfarriaCasino(ctx);
        break;
      case "festivo":
        fanfarriaFestivo(ctx);
        break;
      case "mundial":
        fanfarriaMundial(ctx);
        break;
      case "classic":
      default:
        fanfarriaClassic(ctx);
        break;
    }
  } catch {}
}

export function playConsolation(style: SoundStyle = "classic"): void {
  if (!soundEnabled) return;
  if (style === "silent") return;
  // Slot consolation es fijo (uno solo para todos los estilos). Si
  // hay archivo cargado, ese; sino, sintético seleccionado por estilo
  // para mantener el "feel" del estilo elegido sin tener que cargar
  // un archivo por estilo.
  if (playFile("consolation")) return;
  try {
    const ctx = getCtx();
    switch (style) {
      case "casino":
        consolationCasino(ctx);
        break;
      case "festivo":
      case "mundial":
        consolationFestivo(ctx);
        break;
      case "classic":
      default:
        consolationClassic(ctx);
        break;
    }
  } catch {}
}

// ──────────────────────────────────────────────
// API NUEVA — playEvent(juego, evento, style) con cascada
// ──────────────────────────────────────────────
// Fuente de verdad de qué eventos existen: GAME_SOUND_EVENTS en shared.
// Cascada: slot exacto (style-juego-evento) → slot legacy → sintético.

type EventoSpec = (typeof GAME_SOUND_EVENTS)[number];

// Lookup precomputado por (juego + evento) → spec. Lo construimos una
// vez al cargar el módulo para evitar el .find() en cada playEvent.
const EVENT_SPECS: Map<string, EventoSpec> = new Map(
  GAME_SOUND_EVENTS.map((e) => [`${e.juego}:${e.evento}`, e]),
);

export function playEvent(
  juego: GameType,
  evento: string,
  style: SoundStyle = "classic",
  opts: { intensity?: number } = {},
): void {
  if (!soundEnabled) return;
  if (style === "silent") return;

  const spec = EVENT_SPECS.get(`${juego}:${evento}`);
  if (!spec) {
    // Evento desconocido. No tirar error en runtime — fail silent. El
    // typecheck con la union literal de eventos por juego en el caller
    // captura los typos antes.
    return;
  }

  // 1. Slot exacto del refactor (style × juego × evento).
  if (playFile(gameSoundSlotKey(style, juego, evento), opts.intensity ?? 1)) {
    return;
  }

  // 2. Slot legacy del modelo viejo, si el evento lo tenía. Para los
  //    eventos legacyDependsOnStyle (win, fanfarria), el slot es
  //    win-${style}. Para los demás (tick, spin-start, blink, consolation),
  //    el slot es plano.
  if (spec.legacySlot) {
    const legacyKey = spec.legacyDependsOnStyle
      ? `${spec.legacySlot}-${style}`
      : spec.legacySlot;
    if (playFile(legacyKey, opts.intensity ?? 1)) return;
  }

  // 3. Sintético — generador según el synthetic kind del spec.
  try {
    const ctx = getCtx();
    playSynthetic(ctx, spec.synthetic, style, opts.intensity ?? 1);
  } catch {}
}

function playSynthetic(
  ctx: AudioContext,
  kind: SyntheticFallback,
  style: SoundStyle,
  intensity: number,
): void {
  switch (kind) {
    case "click":
      syntheticClick(intensity);
      return;
    case "spin-start":
      syntheticSpinStart();
      return;
    case "blink":
      syntheticBlink();
      return;
    case "fanfarria":
      // Reusamos las fanfarrias por estilo del modelo viejo. Si el
      // style es uno de los 4 conocidos, suena con su melodía.
      switch (style) {
        case "casino":
          fanfarriaCasino(ctx);
          break;
        case "festivo":
          fanfarriaFestivo(ctx);
          break;
        case "mundial":
          fanfarriaMundial(ctx);
          break;
        default:
          fanfarriaClassic(ctx);
          break;
      }
      return;
    case "consolation":
      switch (style) {
        case "casino":
          consolationCasino(ctx);
          break;
        case "festivo":
        case "mundial":
          consolationFestivo(ctx);
          break;
        default:
          consolationClassic(ctx);
          break;
      }
      return;
    case "scratch":
      syntheticScratch(ctx);
      return;
    case "reveal":
      syntheticReveal(ctx);
      return;
    case "flip":
      syntheticFlip(ctx);
      return;
    case "match":
      syntheticMatch(ctx);
      return;
    case "mismatch":
      syntheticMismatch(ctx);
      return;
    case "lose":
      syntheticLose(ctx);
      return;
    default: {
      const _exhaustive: never = kind;
      return _exhaustive;
    }
  }
}

// ── Sintéticos nuevos del refactor ──

// Rascado: noise filtrado corto. Pensado para llamarlo throttled
// (cada 60-80ms) mientras el dedo se mueve, no como loop continuo.
//
// Diseño del timbre: noise blanco saturado pasado por dos bandpass
// (3000Hz y 5500Hz) sumados — el bandpass único da un "swoosh"
// limpio que sonaba a aire, no a raspado. Dos bandas + Q alto
// agregan rugosidad metálica que sí se siente como rasca de moneda.
// Volumen suave (0.05) porque se dispara muy seguido — si fuera más
// fuerte cansa al cliente.
function syntheticScratch(ctx: AudioContext): void {
  const now = ctx.currentTime;
  const dur = 0.09;
  const bufLen = Math.floor(ctx.sampleRate * dur);
  const buf = ctx.createBuffer(1, bufLen, ctx.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < bufLen; i++) {
    // Noise saturado con tanh para darle más "garra".
    const noise = Math.random() * 2 - 1;
    data[i] = Math.tanh(noise * 2.5);
  }
  const src = ctx.createBufferSource();
  src.buffer = buf;
  // Dos bandas en paralelo para textura áspera.
  const filterLow = ctx.createBiquadFilter();
  filterLow.type = "bandpass";
  filterLow.frequency.value = 3000;
  filterLow.Q.value = 4;
  const filterHigh = ctx.createBiquadFilter();
  filterHigh.type = "bandpass";
  filterHigh.frequency.value = 5500;
  filterHigh.Q.value = 5;
  const mixGain = ctx.createGain();
  mixGain.gain.setValueAtTime(0.08, now);
  // Decay rápido pero no instantáneo — un toque de "cola" hace que
  // suene a fricción, no a click.
  mixGain.gain.exponentialRampToValueAtTime(0.001, now + dur);
  src.connect(filterLow);
  src.connect(filterHigh);
  filterLow.connect(mixGain);
  filterHigh.connect(mixGain);
  mixGain.connect(ctx.destination);
  src.start(now);
}

// Revelado: chord ascendente corto que marca "apareció el premio".
function syntheticReveal(ctx: AudioContext): void {
  const now = ctx.currentTime;
  [523.25, 783.99].forEach((freq, i) => {
    const osc = ctx.createOscillator();
    osc.type = "triangle";
    osc.frequency.value = freq;
    const start = now + i * 0.06;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, start);
    gain.gain.linearRampToValueAtTime(0.18, start + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.001, start + 0.25);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(start);
    osc.stop(start + 0.25);
  });
}

// Flip de carta: tick rápido con un pequeño pitch sweep.
function syntheticFlip(ctx: AudioContext): void {
  const now = ctx.currentTime;
  const osc = ctx.createOscillator();
  osc.type = "triangle";
  osc.frequency.setValueAtTime(660, now);
  osc.frequency.exponentialRampToValueAtTime(440, now + 0.08);
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0, now);
  gain.gain.linearRampToValueAtTime(0.16, now + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.001, now + 0.1);
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.start(now);
  osc.stop(now + 0.1);
}

// Match: chord positivo (3era mayor) corto.
function syntheticMatch(ctx: AudioContext): void {
  const now = ctx.currentTime;
  [523.25, 659.25, 783.99].forEach((freq) => {
    const osc = ctx.createOscillator();
    osc.type = "sine";
    osc.frequency.value = freq;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.15, now + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.35);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(now);
    osc.stop(now + 0.35);
  });
}

// Mismatch: buzz corto descendente.
function syntheticMismatch(ctx: AudioContext): void {
  const now = ctx.currentTime;
  const osc = ctx.createOscillator();
  osc.type = "sawtooth";
  osc.frequency.setValueAtTime(220, now);
  osc.frequency.exponentialRampToValueAtTime(120, now + 0.2);
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0, now);
  gain.gain.linearRampToValueAtTime(0.12, now + 0.02);
  gain.gain.exponentialRampToValueAtTime(0.001, now + 0.22);
  const filter = ctx.createBiquadFilter();
  filter.type = "lowpass";
  filter.frequency.value = 800;
  osc.connect(filter);
  filter.connect(gain);
  gain.connect(ctx.destination);
  osc.start(now);
  osc.stop(now + 0.22);
}

// Lose: descenso triste tipo "wah wah".
function syntheticLose(ctx: AudioContext): void {
  const now = ctx.currentTime;
  [523.25, 466.16, 392.0].forEach((freq, i) => {
    const osc = ctx.createOscillator();
    osc.type = "sawtooth";
    osc.frequency.value = freq;
    const start = now + i * 0.18;
    const dur = 0.4;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, start);
    gain.gain.linearRampToValueAtTime(0.14, start + 0.04);
    gain.gain.exponentialRampToValueAtTime(0.001, start + dur);
    const filter = ctx.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.value = 1500;
    osc.connect(filter);
    filter.connect(gain);
    gain.connect(ctx.destination);
    osc.start(start);
    osc.stop(start + dur);
  });
}

// ──────────────────────────────────────────────
// Fallbacks sintéticos (los originales) — se mantienen para que la
// app suene aun sin archivos subidos. Cuando el user sube un archivo
// para el slot, este código NO se ejecuta.
// ──────────────────────────────────────────────
function syntheticClick(intensity: number): void {
  try {
    const ctx = getCtx();
    const now = ctx.currentTime;
    const bufLen = Math.floor(ctx.sampleRate * 0.06);
    const buf = ctx.createBuffer(1, bufLen, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < buf.length; i++) {
      const t = i / buf.length;
      data[i] = (Math.random() * 2 - 1) * Math.exp(-t * 8) * intensity;
    }
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const gain = ctx.createGain();
    gain.gain.value = 0.35;
    src.connect(gain);
    gain.connect(ctx.destination);
    src.start(now);
  } catch {}
}

// Whoosh corto para spin-start sintético — sweep descendente con noise
// filtrado. Mejor que silencio mientras no haya archivo cargado.
function syntheticSpinStart(): void {
  try {
    const ctx = getCtx();
    const now = ctx.currentTime;
    const dur = 0.35;
    const bufLen = Math.floor(ctx.sampleRate * dur);
    const buf = ctx.createBuffer(1, bufLen, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < bufLen; i++) {
      const t = i / bufLen;
      data[i] = (Math.random() * 2 - 1) * (1 - t);
    }
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const filter = ctx.createBiquadFilter();
    filter.type = "bandpass";
    filter.Q.value = 4;
    filter.frequency.setValueAtTime(2000, now);
    filter.frequency.exponentialRampToValueAtTime(400, now + dur);
    const gain = ctx.createGain();
    gain.gain.value = 0.18;
    src.connect(filter);
    filter.connect(gain);
    gain.connect(ctx.destination);
    src.start(now);
  } catch {}
}

// Chime corto sintético para el blink del ganador.
function syntheticBlink(): void {
  try {
    const ctx = getCtx();
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    osc.type = "triangle";
    osc.frequency.setValueAtTime(880, now);
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.18, now + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.25);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(now);
    osc.stop(now + 0.25);
  } catch {}
}

function fanfarriaClassic(ctx: AudioContext): void {
  const notes = [523.25, 659.25, 783.99, 1046.5];
  notes.forEach((freq, i) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "triangle";
    osc.frequency.value = freq;
    const start = ctx.currentTime + i * 0.12;
    const dur = 0.25;
    gain.gain.setValueAtTime(0, start);
    gain.gain.linearRampToValueAtTime(0.3, start + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.001, start + dur);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(start);
    osc.stop(start + dur);
  });
}

function fanfarriaCasino(ctx: AudioContext): void {
  const notes = [523.25, 698.46, 783.99, 1046.5];
  notes.forEach((freq, i) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "square";
    osc.frequency.value = freq;
    const start = ctx.currentTime + i * 0.1;
    const dur = 0.18;
    gain.gain.setValueAtTime(0, start);
    gain.gain.linearRampToValueAtTime(0.15, start + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.001, start + dur);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(start);
    osc.stop(start + dur);
  });
  const coinStart = ctx.currentTime + 0.5;
  const bufLen = Math.floor(ctx.sampleRate * 0.6);
  const buf = ctx.createBuffer(1, bufLen, ctx.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < bufLen; i++) {
    data[i] = (Math.random() * 2 - 1) * Math.exp(-i / (bufLen * 0.4));
  }
  const src = ctx.createBufferSource();
  src.buffer = buf;
  const filter = ctx.createBiquadFilter();
  filter.type = "bandpass";
  filter.frequency.value = 3000;
  filter.Q.value = 8;
  const gain = ctx.createGain();
  gain.gain.value = 0.25;
  src.connect(filter);
  filter.connect(gain);
  gain.connect(ctx.destination);
  src.start(coinStart);
}

function fanfarriaFestivo(ctx: AudioContext): void {
  const ascendente = [523.25, 587.33, 659.25, 783.99, 880.0, 1046.5];
  ascendente.forEach((freq, i) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "triangle";
    osc.frequency.value = freq;
    const lfo = ctx.createOscillator();
    const lfoGain = ctx.createGain();
    lfo.frequency.value = 5;
    lfoGain.gain.value = freq * 0.015;
    lfo.connect(lfoGain);
    lfoGain.connect(osc.frequency);
    const start = ctx.currentTime + i * 0.08;
    const dur = 0.2;
    gain.gain.setValueAtTime(0, start);
    gain.gain.linearRampToValueAtTime(0.22, start + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.001, start + dur);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(start);
    lfo.start(start);
    osc.stop(start + dur);
    lfo.stop(start + dur);
  });
  const chordStart = ctx.currentTime + 0.55;
  [523.25, 659.25, 783.99, 1046.5].forEach((freq) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "triangle";
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(0, chordStart);
    gain.gain.linearRampToValueAtTime(0.15, chordStart + 0.03);
    gain.gain.exponentialRampToValueAtTime(0.001, chordStart + 0.8);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(chordStart);
    osc.stop(chordStart + 0.8);
  });
}

function fanfarriaMundial(ctx: AudioContext): void {
  const inicio = ctx.currentTime;
  [523.25, 659.25, 783.99].forEach((freq) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sawtooth";
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(0, inicio);
    gain.gain.linearRampToValueAtTime(0.12, inicio + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.001, inicio + 0.3);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(inicio);
    osc.stop(inicio + 0.3);
  });
  const golStart = inicio + 0.4;
  const golDur = 1.4;
  const golOsc = ctx.createOscillator();
  golOsc.type = "sawtooth";
  golOsc.frequency.setValueAtTime(440, golStart);
  golOsc.frequency.exponentialRampToValueAtTime(880, golStart + golDur);
  const lfo = ctx.createOscillator();
  const lfoGain = ctx.createGain();
  lfo.frequency.value = 7;
  lfoGain.gain.value = 40;
  lfo.connect(lfoGain);
  lfoGain.connect(golOsc.frequency);
  const golGain = ctx.createGain();
  golGain.gain.setValueAtTime(0, golStart);
  golGain.gain.linearRampToValueAtTime(0.18, golStart + 0.08);
  golGain.gain.setValueAtTime(0.18, golStart + golDur - 0.2);
  golGain.gain.exponentialRampToValueAtTime(0.001, golStart + golDur);
  const filter = ctx.createBiquadFilter();
  filter.type = "lowpass";
  filter.frequency.value = 2500;
  golOsc.connect(filter);
  filter.connect(golGain);
  golGain.connect(ctx.destination);
  golOsc.start(golStart);
  golOsc.stop(golStart + golDur);
  lfo.start(golStart);
  lfo.stop(golStart + golDur);
}

function consolationClassic(ctx: AudioContext): void {
  const notes = [440, 349.23, 261.63];
  notes.forEach((freq, i) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.value = freq;
    const start = ctx.currentTime + i * 0.18;
    const dur = 0.35;
    gain.gain.setValueAtTime(0, start);
    gain.gain.linearRampToValueAtTime(0.2, start + 0.03);
    gain.gain.exponentialRampToValueAtTime(0.001, start + dur);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(start);
    osc.stop(start + dur);
  });
}

function consolationCasino(ctx: AudioContext): void {
  const notes = [523.25, 466.16, 415.3, 349.23];
  notes.forEach((freq, i) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "square";
    osc.frequency.value = freq;
    const start = ctx.currentTime + i * 0.12;
    const dur = 0.18;
    gain.gain.setValueAtTime(0, start);
    gain.gain.linearRampToValueAtTime(0.1, start + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.001, start + dur);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(start);
    osc.stop(start + dur);
  });
}

function consolationFestivo(ctx: AudioContext): void {
  [392, 466.16, 587.33].forEach((freq) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.value = freq;
    const start = ctx.currentTime;
    const dur = 0.9;
    gain.gain.setValueAtTime(0, start);
    gain.gain.linearRampToValueAtTime(0.12, start + 0.1);
    gain.gain.exponentialRampToValueAtTime(0.001, start + dur);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(start);
    osc.stop(start + dur);
  });
}
